import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { imageUploadService } from "../../../services/imageUploadService";
import {
  createAgentService,
  getAgentByIdService,
  updateAgentService,
} from "../../../services/agentService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useStore from "../../../useStore";

const validationSchema = Yup.object({
  userName: Yup.string().required("Agent name is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  password: Yup.string(),
  email: Yup.string().email("Invalid email"),
  address: Yup.string(),
  userPhoto: Yup.string().url("Invalid URL"),
  agentPhoto: Yup.string().url("Invalid URL"),
  document: Yup.string().url("Invalid URL"),
  aadhaarNumber: Yup.string(),
  aadhaarImage: Yup.string().url("Invalid URL"),
  panNumber: Yup.string(),
  panImage: Yup.string().url("Invalid URL"),
  accountNumber: Yup.string(),
  accountName: Yup.string(),
  IFSC: Yup.string(),
  branch: Yup.string(),
  familyPersonName: Yup.string(),
  relation: Yup.string(),
  familyPersonAddress: Yup.string(),
  familyPersonPhoneNumber: Yup.string(),
});

const AgentForm = () => {
  const navigate = useNavigate();
  const { editUserId, isAgentUpdate } = useStore();
  const [agentDetails, setAgentDetails] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [email, setEmail] = useState();

  const handleCheckboxChange = (event, setFieldValue) => {
    const { name, checked } = event.target;
    setFieldValue(`role.${name}`, checked); // Update the role object in Formik's field value
  };

  const fetchAgentDetails = async () => {
    try {
      const response = await getAgentByIdService(editUserId);
      console.log(response);
      setAgentDetails(response);
      response?.contacts.forEach((contact) => {
        switch (contact?.contactType) {
          case "phone":
            setPhoneNumber(contact.contactValue);
            break;
          case "email":
            setEmail(contact.contactValue);
            break;

          default:
            break;
        }
      });
      //setLoading(false);
    } catch (err) {
      toast.error("Error Retriving Agent Details");
      //setError(err.message);
      //setLoading(false);
    }
  };

  useEffect(() => {
    if (isAgentUpdate) fetchAgentDetails();
  }, [editUserId]);

  const handleSubmit = async (values, actions) => {
    try {
      if (isAgentUpdate) {
        const updateAgent = await updateAgentService(values, editUserId);
        if (updateAgent.status === "success") {
          toast.success("Agent created successfully");
          navigate("/agent");
        } else {
          toast.error("Failed to create agent");
        }
      } else {
        const createAgent = await createAgentService(values);
        if (createAgent.status === "success") {
          toast.success("Agent created successfully");
          navigate("/agent");
        } else {
          toast.error("Failed to create agent");
        }
        actions.setSubmitting(false);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const PhotoField = ({ name, label, handleFileUpload }) => (
    <Grid item xs={3}>
      <Typography mt={1}>{label}</Typography>
      <Field name={name}>
        {({ field, form }) => (
          <TextField
            fullWidth
            size="small"
            type="file"
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.875rem", // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              handleFileUpload(file, form.setFieldValue, field.name);
            }}
          />
        )}
      </Field>
    </Grid>
  );

  const handleFileUpload = async (file, setFieldValue, fieldName) => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    const imageUrl = await imageUploadService(formData);
    setFieldValue(fieldName, imageUrl);
  };

  return (
    <Paper sx={{ background: "#fff" }}>
      <Box sx={{ width: "100%", p: 3 }}>
        <Formik
          enableReinitialize // Allow reinitializing with new values
          initialValues={{
            userName: agentDetails?.user?.userName || "",
            phoneNumber: phoneNumber || "",
            password: "",
            email: email || "",
            address: agentDetails?.user?.address || "",
            userPhoto: "",
            document: "",
            aadhaarNumber: agentDetails?.agentDocuments?.aadhaarNumber || "",
            aadhaarPhoto: "",
            panNumber: agentDetails?.agentDocuments?.panNumber || "",
            panPhoto: "",
            accountNumber: agentDetails?.agentBankDetails?.accountNumber || "",
            accountName: agentDetails?.agentBankDetails?.accountName || "",
            IFSC: agentDetails?.agentBankDetails?.IFSC || "",
            branch: agentDetails?.agentBankDetails?.branch || "",
            familyPersonName:
              agentDetails?.agentFamilyDetails?.familyPersonName || "",
            relation: agentDetails?.agentFamilyDetails?.relation || "",
            familyPersonAddress:
              agentDetails?.agentFamilyDetails?.familyPersonAddress || "",
            familyPersonPhoneNumber:
              agentDetails?.agentFamilyDetails?.familyPersonPhoneNumber || "",
            role: {
              collection: agentDetails?.user?.role?.collection || false,
              default: agentDetails?.user?.role?.default || false,
              verify: agentDetails?.user?.role?.verify || false,
              anotherRole: agentDetails?.user?.role?.anotherRole || false,
            },
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Box sx={{ px: 2 }}>
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography mt={1}>Agent Name</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="userName"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Phone Number</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="phoneNumber"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                      inputProps={{
                        maxLength: 10, // Limits the input to 10 characters
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Password</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="password"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Email</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="email"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <PhotoField
                    name="userPhoto"
                    label="Agent Photo"
                    handleFileUpload={handleFileUpload}
                  />
                  <Grid item xs={3}>
                    <Typography mt={1}>Address</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="address"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>
                  <PhotoField
                    name="document"
                    label="Document"
                    handleFileUpload={handleFileUpload}
                  />

                  <Grid item xs={3}>
                    <Typography mt={1}>Aadhaar Number</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="aadhaarNumber"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>
                  <PhotoField
                    name="aadhaarPhoto"
                    label="Aadhaar Image"
                    handleFileUpload={handleFileUpload}
                  />

                  <Grid item xs={3}>
                    <Typography mt={1}>PAN Number</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="panNumber"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <PhotoField
                    name="panPhoto"
                    label="PAN Image"
                    handleFileUpload={handleFileUpload}
                  />

                  <Grid item xs={3}>
                    <Typography mt={1}>Account Number</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="accountNumber"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Account Name</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="accountName"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>IFSC</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="IFSC"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Branch</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="branch"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Family Person Name</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="familyPersonName"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Relation</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="relation"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Family Person Address</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="familyPersonAddress"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography mt={1}>Family Person Phone Number</Typography>
                    <Field
                      fullWidth
                      size="small"
                      name="familyPersonPhoneNumber"
                      as={TextField}
                      sx={{
                        "& .MuiInputBase-root": {
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography mt={2}>Roles</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.role.collection}
                          onChange={(event) =>
                            handleCheckboxChange(event, setFieldValue)
                          }
                          name="collection"
                        />
                      }
                      label="Collection"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.role.default}
                          onChange={(event) =>
                            handleCheckboxChange(event, setFieldValue)
                          }
                          name="default"
                        />
                      }
                      label="Default"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.role.verify}
                          onChange={(event) =>
                            handleCheckboxChange(event, setFieldValue)
                          }
                          name="verify"
                        />
                      }
                      label="Verify"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.role.anotherRole}
                          onChange={(event) =>
                            handleCheckboxChange(event, setFieldValue)
                          }
                          name="anotherRole"
                        />
                      }
                      label="Another Role"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Box>
          )}
        </Formik>
      </Box>
    </Paper>
  );
};

export default AgentForm;
